/**
 * The device agent.
 *
 * Loaded by the application layout on every page, so it has two hard
 * obligations: it must do nothing at all on a computer that has never been
 * enrolled, and it must not drag React onto pages that do not have it. The
 * enrollment UI is therefore behind a dynamic import that only the enrollment
 * page triggers.
 *
 * On an enrolled computer it starts the heartbeat, which is the only thing
 * station presence is derived from.
 */
import { isEnrolled } from "../device/enrollment"
import { startHeartbeat, type HeartbeatHandle, type HeartbeatReport } from "../device/heartbeat"
import { readIdentity } from "../device/store"
import { recordTrustedInstant } from "../offline/clock"
import { SCHEMA_VERSION } from "../db/schema"
import { openOfflineDatabase } from "../db/open"
import { outboxReport, snapshotReport } from "../device/reported-state"
import { evaluateReadiness } from "../offline/readiness/evaluator"
import { storageState } from "../offline/readiness/storage"

/**
 * Each of these is a whole page rendered by React, and no more than one of them
 * is ever on screen. They are dynamic imports so that the overwhelming majority
 * of pages — which have none of these elements — pay nothing for them.
 */
const PAGE_ROOTS = {
  "device-enroll-root": () => import("../device/EnrollmentPage").then(module => module.mountEnrollment),
  "device-readiness-root": () => import("../offline/readiness/ReadinessPage").then(module => module.mountReadiness),
  "device-offline-login-root": () => import("../offline/auth/OfflineLoginPage").then(module => module.mountOfflineLogin),
  "mobile-offline-login-root": () => import("../offline/auth/MobileCodePage").then(module => module.mountMobileCode)
} as const

/**
 * The built bundle's own hashed filename. It is a real build identifier — it
 * changes exactly when the frontend changes — and it costs nothing to report,
 * where a hand-maintained version string would drift.
 */
export const APP_VERSION = import.meta.url.split("/").pop() ?? "unknown"

async function boot(): Promise<void> {
  for (const [ rootId, load ] of Object.entries(PAGE_ROOTS)) {
    const element = document.getElementById(rootId)
    if (!element) continue

    const mount = await load()
    mount(element)
    break
  }

  if (await isEnrolled()) await registerShellCache()

  await startAgent()
}

let agent: HeartbeatHandle | null = null

/**
 * The application shell is only cached on a computer that has a reason to work
 * without a network. Registering it everywhere would give every browser in the
 * organisation an offline fallback page telling it that it is not a nominated
 * station computer, which is true and useless.
 */
async function registerShellCache(): Promise<void> {
  if (!("serviceWorker" in navigator)) return

  try {
    await navigator.serviceWorker.register("/service-worker")
  } catch {
    // A shell that could not be cached is a readiness problem, reported there.
  }
}

/**
 * What this computer tells an administrator about itself.
 *
 * Readiness is re-derived from the database on every heartbeat rather than
 * cached from the last bootstrap, so a device that has quietly lost something —
 * an expired snapshot, an evicted store — starts saying so within one interval
 * instead of the next time somebody happens to open the readiness page.
 */
async function reportState(): Promise<HeartbeatReport> {
  const identity = await readIdentity()

  // A refused upgrade is the one fault this computer cannot report by measuring
  // itself: every other reason is derived from the database, and this is the
  // database declining to open. Reported rather than thrown, so an administrator
  // sees the machine go not-ready within one interval instead of seeing it go
  // silent — which looks like a computer that has been switched off.
  const { refusal } = await openOfflineDatabase()
  if (refusal !== null) {
    return {
      appVersion: APP_VERSION,
      indexeddbSchemaVersion: SCHEMA_VERSION,
      offlineReady: false,
      offlineReadyReason: "migration_refused"
    }
  }

  const readiness = await evaluateReadiness({
    enrolled: identity !== null,
    credentialExpiresAt: identity?.credential?.claims?.expires_at ?? null,
    expectedRegisterCount: null,
    // Reported to an administrator, which is the whole point of a quota warning
    // on a fleet of station computers nobody is standing in front of.
    storage: storageState
  })

  return {
    appVersion: APP_VERSION,
    indexeddbSchemaVersion: SCHEMA_VERSION,
    offlineReady: readiness.ready,
    offlineReadyReason: readiness.reason ?? undefined,
    // The two facts read out of IndexedDB rather than derived from readiness.
    // Both were declared and permitted from the beginning and never populated —
    // see `device/reported-state.ts`.
    ...(await outboxReport()),
    ...(await snapshotReport())
  }
}

async function startAgent(): Promise<void> {
  agent?.stop()
  agent = await startHeartbeat({
    report: reportState,
    // Every successful heartbeat is a signature-verified exchange, which makes
    // the time it carries the anchor the offline clock ratchets forward from.
    // Without this the device has no trustworthy sense of "now" at all, and
    // every expiry it enforces later is enforced against a clock nobody checked.
    onResponse: response => void recordTrustedInstant(Date.parse(response.serverTime)),
    // A revoked computer stops on the spot rather than retrying with a key the
    // server has thrown away. The page it is on keeps working as an ordinary
    // browser session; what it loses is its identity as this station's machine.
    onDisowned: () => {
      agent = null
      console.warn("[eRegister] This computer is no longer enrolled. Ask an administrator to enrol it again.")
    },
    onError: () => {
      // Losing the server is the ordinary condition here, not an incident.
    }
  })
}

// Turbo replaces the body without re-evaluating modules, so the agent survives
// navigation on its own; nothing needs re-starting per page.
void boot()
